"use client";
import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { SelectButton } from "@/components/Elements/SelectButton";
import {
  categories,
  ethnicityOptions as ethnicities,
  sexOptions as sex,
} from "@/utils/DefaultValues";
import "/src/containers/categories/categories.scss";
import toast from "react-hot-toast";
interface Attributes {
  ethnicity: string[];
  sex: string[];
  category: string[];
  startYear: string;
  endYear: string;
  startTarget: string;
  endTarget: string;
}

export const Filters = ({
  searchParams: sp,
}: {
  searchParams: { [key: string]: string | undefined };
}) => {
  const [attributes, setAttributes] = useState<Attributes>({
    ethnicity: sp.ethnicity ? sp.ethnicity.split(",") : [],
    sex: sp.sex ? sp.sex.split(",") : [],
    category: sp.category ? sp.category.split(",") : [],
    startYear: sp.startYear || "",
    endYear: sp.endYear || "",
    startTarget: sp.startTarget || "",
    endTarget: sp.endTarget || "",
  });
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const handleAttributes = (
    value: string,
    param: string,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (
      param === "startTarget" ||
      param === "endTarget" ||
      param === "startYear" ||
      param === "endYear"
    ) {
      if (value.trim() !== "" && !/^\d{0,4}$/.test(value)) {
        if (e && e.target) {
          e.target.blur();
        }
        toast.error("Please enter a valid year (YYYY).");
        return;
      }
      const newValue =
        value.trim().toUpperCase() === "ALL" || value.trim() === ""
          ? ""
          : value;

      setAttributes((prev) => ({ ...prev, [param]: newValue }));
      return;
    }
    if (param === "ethnicity" || param === "category" || param === "sex") {
      const currentArray = attributes[param] as string[];
      if (currentArray.includes(value)) {
        setAttributes((prev) => ({
          ...prev,
          [param]: currentArray.filter((e) => e !== value),
        }));
        return;
      }
      setAttributes((prev) => ({
        ...prev,
        [param]: [...currentArray, value],
      }));
      return;
    } else {
      setAttributes((prev) => ({ ...prev, [param]: value }));
    }
  };

  const handleParams = () => {
    const startYear = attributes.startYear
      ? parseInt(attributes.startYear)
      : undefined;
    const endYear = attributes.endYear
      ? parseInt(attributes.endYear)
      : undefined;
    if (
      attributes.sex.length === 0 ||
      attributes.category.length === 0 ||
      attributes.ethnicity.length === 0
    ) {
      toast.error("Please select at least one option for each filter.");
      return;
    }
    const params = new URLSearchParams(searchParams);
    Object.entries(attributes).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.join(","));
        } else {
          params.delete(key);
        }
      } else {
        if (value) params.set(key, value);
        else params.delete(key);
      }
    });
    router.push(`${pathname}?${params.toString()}`);
  };
  const handleSelectAll = (
    param: "ethnicity" | "category",
    allOptions: string[]
  ) => {
    setAttributes((prev) => {
      const currentArray = prev[param];
      if (currentArray.length === allOptions.length) {
        return { ...prev, [param]: [] };
      } else {
        return { ...prev, [param]: allOptions };
      }
    });
  };
  return (
    <div className="p-4 d-flex flex-column align-items-center justify-content-center gap-16 galleryies-by-category-filters-container">
      <div className="header mb-4 max-w-[90%] w-full items-center flex gap-16 flex-col justify-center">
        <h3 className="text-center mb-3 font-semibold">Ethnicity</h3>
        <div
          className="flex justify-center cursor-pointer items-center gap-2"
          onClick={() => handleSelectAll("ethnicity", [...ethnicities])}
        >
          <SelectButton
            selected={attributes.ethnicity.length === ethnicities.length}
            disabled={false}
          />
          <span className="text-sm sm:!text-3xl">Select All</span>
        </div>

        <div className="flex flex-wrap gap-6 mx-auto">
          {ethnicities.map((eth, i) => (
            <div
              key={i}
              className="flex cursor-pointer items-center gap-2"
              onClick={() => handleAttributes(eth, "ethnicity")}
            >
              <SelectButton
                selected={attributes.ethnicity.includes(eth)}
                disabled={false}
              />
              <span className="text-sm md:!text-3xl">{eth}</span>
            </div>
          ))}
        </div>
        <h3 className="text-center font-semibold">Categories</h3>
        <div
          className="flex items-center cursor-pointer justify-center gap-2"
          onClick={() => handleSelectAll("category", categories)}
        >
          <SelectButton
            selected={attributes.category.length === categories.length}
            disabled={false}
          />
          <span className="text-sm sm:!text-3xl">Select All</span>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] w-full gap-6 md:gap-14">
          {categories.map((c, i) => (
            <div
              key={i}
              className="flex cursor-pointer items-center gap-2"
              onClick={() => handleAttributes(c, "category")}
            >
              <SelectButton
                selected={attributes.category.includes(c)}
                disabled={false}
              />
              <span className="text-sm md:!text-3xl">{c}</span>
            </div>
          ))}
        </div>
        <h3 className="text-center mb-3 font-semibold">Sexes</h3>
        <div className="flex flex-wrap gap-6 mx-auto">
          {sex.map((s, i) => (
            <div
              key={i}
              className="flex items-center cursor-pointer gap-2"
              onClick={() => handleAttributes(s, "sex")}
            >
              <SelectButton
                selected={attributes.sex.includes(s)}
                disabled={false}
              />
              <span className="text-sm md:!text-3xl">{s}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Filtro de años */}
      <div className="year-filter-container">
        <h2 className="text-center mb-3">Birth dates range</h2>
        <div className="input-container flex gap-2 items-center justify-center">
          <label htmlFor="startYear">From</label>
          <input
            type="text"
            name="startYear"
            id="startYear"
            value={attributes.startYear === "" ? "ALL" : attributes.startYear}
            placeholder="YYYY"
            onChange={(e) => handleAttributes(e.target.value, "startYear")}
            onBlur={(e) => {
              if (e.target.value.trim() === "") {
                // cuando el input se queda vacío y pierde foco, muestro "ALL"
                e.target.value = "ALL";
              }
            }}
            onFocus={(e) => {
              if (e.target.value === "ALL") {
                // cuando el usuario hace focus en "ALL", lo limpio para que pueda escribir
                e.target.value = "";
              }
            }}
          />
          <label htmlFor="endYear">to</label>
          <input
            type="text"
            name="endYear"
            id="endYear"
            value={attributes.endYear === "" ? "ALL" : attributes.endYear}
            placeholder="YYYY"
            onChange={(e) => handleAttributes(e.target.value, "endYear")}
            onBlur={(e) => {
              if (e.target.value.trim() === "") {
                // cuando el input se queda vacío y pierde foco, muestro "ALL"
                e.target.value = "ALL";
              }
            }}
            onFocus={(e) => {
              if (e.target.value === "ALL") {
                // cuando el usuario hace focus en "ALL", lo limpio para que pueda escribir
                e.target.value = "";
              }
            }}
          />
        </div>
        <div>
          <h2 className="text-center mb-3">Target years range</h2>
          <div className="input-container flex gap-2 items-center justify-center">
            <label htmlFor="startTarget">From</label>
            <input
              type="string"
              name="startTarget"
              id="startTarget"
              value={
                attributes.startTarget === "" ? "ALL" : attributes.startTarget
              }
              placeholder="YYYY"
              onChange={(e) => handleAttributes(e.target.value, "startTarget")}
              onBlur={(e) => {
                if (e.target.value.trim() === "") {
                  e.target.value = "ALL";
                }
              }}
              onFocus={(e) => {
                if (e.target.value === "ALL") {
                  e.target.value = "";
                }
              }}
            />
            <label htmlFor="endTarget">to</label>
            <input
              type="string"
              name="endTarget"
              id="endTarget"
              value={attributes.endTarget === "" ? "ALL" : attributes.endTarget}
              placeholder="YYYY"
              onChange={(e) => handleAttributes(e.target.value, "endTarget")}
              onBlur={(e) => {
                if (e.target.value.trim() === "") {
                  e.target.value = "ALL";
                }
              }}
              onFocus={(e) => {
                if (e.target.value === "ALL") {
                  e.target.value = "";
                }
              }}
            />
          </div>
        </div>
      </div>

      <button id="search-btn" className="btn linear-bg" onClick={handleParams}>
        SEARCH
      </button>
    </div>
  );
};
